import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import store from "./store";
import AccountMspa from "./pages/Account/AccountMspa";
import ProjectsMspa from "./pages/Projects/ProjectsMspa";
import ClientsMspa from "./pages/Admin/Clients/ClientsMspa";
import SalesExecutivesMspa from "./pages/Admin/SalesExecutives/SalesExecutivesMspa";
import BusinessUnitsMspa from "./pages/Admin/BusinessUnits/BusinessUnitsMspa";
import CurrenciesMspa from "./pages/Admin/Currencies/CurrenciesMspa";
import WebhookSubscriptionsMspa from "./pages/Admin/WebhookSubscriptions/WebhookSubscriptionsMspa";
import LinesOfBusinessMspa from "./pages/Admin/LinesOfBusiness/LinesOfBusinessMspa";
import LanguageFieldsMspa from "./pages/Admin/LanguageFields/LanguageFieldsMspa";
import ServiceConditionsMspa from "./pages/Admin/ServiceConditions/ServiceConditionsMspa";
import QuestionnairesMspa from "./pages/Admin/Questionnaires/QuestionnairesMspa";
import TextBlocksMspa from "./pages/Admin/TextBlocks/TextBlocksMspa";
import AttachmentTypesMspa from "./pages/Admin/AttachmentTypes/AttachmentTypesMspa";
import PaymentTermsMspa from "./pages/Admin/PaymentTerms/PaymentTermsMspa";
import RateTablesMspa from "./pages/Admin/RateTables/RateTablesMspa";
import ExpenseCategoriesMspa from "./pages/Admin/ExpenseCategories/ExpenseCategoriesMspa";
import PaymentCreditsMspa from "./pages/Admin/PaymentCredits/PaymentCreditsMspa";
import ResourcesMspa from "./pages/Admin/Resources/ResourcesMspa";
import GovernancesMspa from "./pages/GovernanceCatalogV2/GovernancesMspa";
import PhasesMspa from "./pages/GovernanceCatalogV2/PhasesMspa";
import RolesMspa from "./pages/Admin/Roles/RolesMspa";
import OpenRouterKeysMspa from "./pages/Admin/OpenRouterKeys/OpenRouterKeysMspa";
import ProjectApprovalV1Mspa from "./pages/Admin/ProjectApprovalV1Mspa";
import ProjectApprovalV2Mspa from "./pages/Admin/ProjectApprovalV2Mspa/ProjectApprovalV2Mspa";
import useFlags from "./common/hooks/useFlags";

// Picks V2 (the unified ApprovalLevel UI) when release.unified-approval-workflow
// is on; otherwise renders V1 (the legacy LobApproval-based UI). Mounted at
// both /admin/lob_approvals/* and /admin/approval_levels/* so either URL
// respects the flag — clean cutover when the flag flips, no broken bookmarks
// in the meantime.
//
// Note: both the LaunchDarkly SDK and the local useFlags shim camelCase flag
// keys, so `release.unified-approval-workflow` → `releaseUnifiedApprovalWorkflow`.
const FlaggedProjectApprovalMspa = (props) => {
  const { releaseUnifiedApprovalWorkflow } = useFlags();
  return releaseUnifiedApprovalWorkflow ? (
    <ProjectApprovalV2Mspa {...props} />
  ) : (
    <ProjectApprovalV1Mspa {...props} />
  );
};
import PromptOverridesMspa from "./pages/Admin/PromptOverrides/PromptOverridesMspa";
import AccountsMspa from "./pages/Site/Accounts/AccountsMspa";
import TeamsMspa from "./pages/Admin/Teams/TeamsMspa";
import UserDefinedFieldsMspa from "./pages/GovernanceCatalogV2/UserDefinedFieldsMspa";
import LaunchDarklyProvider from "./common/launchDarkly/LaunchDarklyProvider";
import SurveyMspa from "./pages/Surveys/SurveyMspa";
import { Dashboard } from "./pages/DashboardV2";
import { ThemeProvider } from "@emotion/react";
import theme from "./themes/theme";
import AuthorizationProvider from "./common/providers/AuthorizationProvider";
import Products from "./pages/Products";
import ConnectedApps from "./pages/Admin/ConnectedApps";
import { TravelAndExpenseV2Mspa } from "./pages/TravelAndExpenseV2";
import { GovernanceV2Mspa } from "./pages/GovernanceV2";
import { VendorQuotesV2Mspa } from "./pages/VendorQuotesV2";
import { ProfessionalServicesMspa } from "./pages/Admin/ProfessionalServices";
import { DocumentTemplatesMspa } from "./pages/Admin/DocumentTemplates";
import { ReleaseFlagsMspa } from "./pages/Admin/ReleaseFlags";
import { ProjectVersionsV2Mspa } from "./pages/ProjectVersionsV2";
import CollaboratorsV2 from "./pages/CollaboratorsV2/CollaboratorsV2";
import Standards from "./pages/Projects/ServicesV2/Standards";
import Blueprints from "./pages/Projects/ServicesV2/Blueprints";
import VendorsV2Mspa from "./pages/Admin/VendorsV2/VendorsV2Mspa";
import ProductsV2Mspa from "./pages/Admin/ProductsV2/ProductsV2Mspa";
import ResourcesV2Mspa from "./pages/ResourcesV2/ResourcesV2Mspa";
import ProjectSurveysMspa from "./pages/ProjectSurveysV2/ProjectSurveysMspa";
import { Show } from "./pages/ProjectServicesV2/Show";
import CustomerSummaryV2 from "./pages/CustomerSummaryV2/CustomerSummaryV2";
import NotesV2Mspa from "./pages/NotesV2";
import ProjectLocationsV2Mspa from "./pages/ProjectLocationsV2";
import ProjectContactsV2Mspa from "./pages/ProjectContactsV2";
import { ProjectAttachmentsMspa } from "./pages/Projects/ProjectAttachments";
import PaymentCreditsV2Mspa from "./pages/Pricing/PaymentCreditsV2";
import ThirdPartyPricingV2Mspa from "./pages/Pricing/ThirdPartyPricingV2";
import ProductPricingV2Mspa from "./pages/Pricing/ProductPricingV2";
import ProjectPricingV2Mspa from "./pages/Pricing/ProjectPricingV2";
import MsPricingV2Mspa from "./pages/Pricing/MsPricingV2";
import PsPricingV2Mspa from "./pages/Pricing/PsPricingV2";
import WorkBreakdownV2Mspa from "./pages/WorkBreakdownV2";
import ProjectApprovalsV2Mspa from "./pages/ProjectApprovalsV2";
import ProjectConditions from "./pages/ConditionsMspa/ProjectConditions";

const App = (props) => {
  const { account_slug, project_id } = props;

  if (!account_slug || account_slug === "" || account_slug === undefined) {
    throw new Error("!! Extreme danger !! account_slug is required");
  }

  return (
    <Provider store={store}>
      <AuthorizationProvider account_slug={account_slug}>
        <ThemeProvider theme={theme}>
          <LDProvider
            clientSideID={
              process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
            }
          >
            <LaunchDarklyProvider
              accountSlug={account_slug}
              clientSideID={
                process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
              }
            >
              <Router>
                <Routes>
                  <Route path="/home" element={<Dashboard {...props} />} />
                  <Route path="/users/*" element={<AccountMspa {...props} />} />
                  <Route
                    path="/projects/:id/project_materials/*"
                    element={<Products {...props} />}
                  />
                  <Route
                    path="/projects/*"
                    element={<ProjectsMspa {...props} />}
                  />
                  <Route
                    path="/surveys/*"
                    element={<SurveyMspa {...props} />}
                  />
                  <Route
                    path="/admin/account_connections/*"
                    element={<ConnectedApps {...props} />}
                  />
                  <Route
                    path="/admin/teams/*"
                    element={<TeamsMspa {...props} />}
                  />
                  <Route
                    path="/admin/clients/*"
                    element={<ClientsMspa {...props} />}
                  />
                  <Route
                    path="/admin/sales_executives/*"
                    element={<SalesExecutivesMspa {...props} />}
                  />
                  <Route
                    path="/admin/business_units/*"
                    element={<BusinessUnitsMspa {...props} />}
                  />
                  <Route
                    path="/admin/governances/*"
                    element={<GovernancesMspa {...props} />}
                  />
                  <Route
                    path="/admin/currencies/*"
                    element={<CurrenciesMspa {...props} />}
                  />
                  <Route
                    path="/admin/webhook_subscriptions/*"
                    element={<WebhookSubscriptionsMspa {...props} />}
                  />
                  <Route
                    path="/admin/sections/*"
                    element={<LinesOfBusinessMspa {...props} />}
                  />
                  <Route
                    path="/admin/language_fields/*"
                    element={<LanguageFieldsMspa {...props} />}
                  />
                  <Route
                    path="/admin/service_conditions/*"
                    element={<ServiceConditionsMspa {...props} />}
                  />
                  <Route
                    path="/admin/questionnaires/*"
                    element={<QuestionnairesMspa {...props} />}
                  />
                  <Route
                    path="/admin/text_blocks/*"
                    element={<TextBlocksMspa {...props} />}
                  />
                  <Route
                    path="/admin/attachment_types/*"
                    element={<AttachmentTypesMspa {...props} />}
                  />
                  <Route
                    path="/admin/payment_terms/*"
                    element={<PaymentTermsMspa {...props} />}
                  />
                  <Route
                    path="/admin/rate_tables/*"
                    element={<RateTablesMspa {...props} />}
                  />
                  <Route
                    path="/admin/expense_categories/*"
                    element={<ExpenseCategoriesMspa {...props} />}
                  />
                  <Route
                    path="/admin/payment_credits/*"
                    element={<PaymentCreditsMspa {...props} />}
                  />
                  <Route
                    path="/admin/resources/*"
                    element={<ResourcesMspa {...props} />}
                  />
                  <Route
                    path="/admin/lob_approvals/*"
                    element={<FlaggedProjectApprovalMspa {...props} />}
                  />
                  <Route
                    path="/admin/approval_levels/*"
                    element={<FlaggedProjectApprovalMspa {...props} />}
                  />
                  <Route
                    path="/admin/roles/*"
                    element={<RolesMspa {...props} />}
                  />
                  <Route
                    path="/admin/open_router_keys/*"
                    element={<OpenRouterKeysMspa {...props} />}
                  />
                  <Route
                    path="/admin/phases/*"
                    element={<PhasesMspa {...props} />}
                  />
                  <Route
                    path="/admin/project_variables/*"
                    element={<UserDefinedFieldsMspa {...props} />}
                  />
                  <Route
                    path="/admin/prompt_overrides/*"
                    element={<PromptOverridesMspa {...props} />}
                  />
                  <Route
                    path="/site/accounts/*"
                    element={<AccountsMspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_expenses/*"
                    element={<TravelAndExpenseV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/sow_outcome/*"
                    element={<CustomerSummaryV2 {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_governances/*"
                    element={<GovernanceV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/quotes/*"
                    element={<VendorQuotesV2Mspa {...props} />}
                  />
                  <Route
                    path="/admin/tasks/*"
                    element={
                      <ProfessionalServicesMspa
                        {...props}
                        serviceType="professional_services"
                      />
                    }
                  />
                  <Route
                    path="/admin/managed_services/*"
                    element={
                      <ProfessionalServicesMspa
                        {...props}
                        serviceType="managed_services"
                      />
                    }
                  />
                  <Route
                    path="/admin/document_templates/*"
                    element={<DocumentTemplatesMspa {...props} />}
                  />
                  <Route
                    path="/admin/release_flags/*"
                    element={<ReleaseFlagsMspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_versions/*"
                    element={<ProjectVersionsV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_collaborators/*"
                    element={<CollaboratorsV2 {...props} />}
                  />
                  <Route
                    path="/projects/:id/standard_tasks/new"
                    element={<Standards {...props} />}
                  />
                  <Route
                    path="/projects/:id/task_blueprints/new"
                    element={<Blueprints {...props} />}
                  />
                  <Route
                    path="/admin/vendors/*"
                    element={<VendorsV2Mspa {...props} />}
                  />
                  <Route
                    path="/admin/materials/*"
                    element={<ProductsV2Mspa {...props} />}
                  />
                  <Route
                    path="projects/:id/project_surveys/*"
                    element={<ProjectSurveysMspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_resources/*"
                    element={<ResourcesV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/notes/*"
                    element={<NotesV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_locations/*"
                    element={<ProjectLocationsV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_contacts/*"
                    element={<ProjectContactsV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/attachments/*"
                    element={<ProjectAttachmentsMspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_credits/*"
                    element={<PaymentCreditsV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/third_party_pricing/*"
                    element={<ThirdPartyPricingV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_material_pricings/*"
                    element={<ProductPricingV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_pricing/*"
                    element={<ProjectPricingV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/service_summary/*"
                    element={<MsPricingV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/sow_pricings/*"
                    element={<PsPricingV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/summary/*"
                    element={<WorkBreakdownV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_approvals/*"
                    element={<ProjectApprovalsV2Mspa {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_conditions/*"
                    element={<ProjectConditions {...props} />}
                  />
                  <Route
                    path="/projects/:id/project_editor/*"
                    element={
                      <Show service_type="professional_services" {...props} />
                    }
                  />
                  <Route
                    path="/projects/:id/managed_service/*"
                    element={
                      <Show service_type="managed_services" {...props} />
                    }
                  />
                </Routes>
              </Router>
            </LaunchDarklyProvider>
          </LDProvider>
        </ThemeProvider>
      </AuthorizationProvider>
    </Provider>
  );
};

export default App;
